import React, { useEffect } from 'react';
import FuturaSmileIcon from './icons/FuturaSmileIcon';

// --- PROPS INTERFACE ---
interface CaseReportPDFProps {
    reportData: any;
    originalImageUrl: string;
    generatedImageUrl: string;
    smileRating: any;
    afterSmileRating: any;
    onRendered: () => void;
}

// --- EMBEDDED ICONS (Self-contained for PDF rendering) ---
const CheckCircleIcon: React.FC<{ className?: string }> = ({ className }) => (
    <svg className={className} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
);
const PathIcon: React.FC<{ className?: string }> = ({ className }) => (
    <svg className={className} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 4.5a.75.75 0 00-1.5 0v1.5h-1.5a.75.75 0 000 1.5h1.5v1.5a.75.75 0 001.5 0v-1.5h1.5a.75.75 0 000-1.5h-1.5v-1.5zm15 0a.75.75 0 00-1.5 0v1.5h-1.5a.75.75 0 000 1.5h1.5v1.5a.75.75 0 001.5 0v-1.5h1.5a.75.75 0 000-1.5h-1.5v-1.5zm-6.75 3a.75.75 0 00-1.5 0v1.5h-1.5a.75.75 0 000 1.5h1.5v1.5a.75.75 0 001.5 0v-1.5h1.5a.75.75 0 000-1.5h-1.5v-1.5zm-3.75 9a.75.75 0 00-1.5 0v1.5h-1.5a.75.75 0 000 1.5h1.5v1.5a.75.75 0 001.5 0v-1.5h1.5a.75.75 0 000-1.5h-1.5v-1.5zm15 0a.75.75 0 00-1.5 0v1.5h-1.5a.75.75 0 000 1.5h1.5v1.5a.75.75 0 001.5 0v-1.5h1.5a.75.75 0 000-1.5h-1.5v-1.5z" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 12a.75.75 0 00-1.5 0v1.5h-1.5a.75.75 0 000 1.5h1.5v1.5a.75.75 0 001.5 0v-1.5h1.5a.75.75 0 000-1.5h-1.5v-1.5zm3.75-1.5a.75.75 0 000 1.5h1.5v1.5a.75.75 0 001.5 0v-1.5h1.5a.75.75 0 000-1.5h-1.5v-1.5a.75.75 0 00-1.5 0v1.5h-1.5z" />
    </svg>
);
const NextStepsIcon: React.FC<{ className?: string }> = ({ className }) => (
    <svg className={className} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M15.59 14.37a6 6 0 01-5.84 7.38v-4.8m5.84-2.58a14.98 14.98 0 006.16-12.12A14.98 14.98 0 009.63 2.18a14.98 14.98 0 00-2.18 6.16m5.84 2.58v4.8m0-4.8a6 6 0 01-7.38-5.84m7.38 5.84L15 11.25m-2.25 2.25L11.25 15m3.75 0l2.25 2.25M15 11.25l2.25-2.25m0 0l2.25 2.25m-2.25-2.25L15 6.75 12.75 9l2.25 2.25z" />
    </svg>
);
const TransformationArrowIcon: React.FC<{ className?: string }> = ({ className }) => (
    <svg className={className} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
    </svg>
);
const ChartBarIcon: React.FC<{ className?: string }> = ({ className }) => (
    <svg className={className} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" />
    </svg>
);


// --- CORE LAYOUT COMPONENTS ---
const Page: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = '' }) => (
    <div
        className={`pdf-page relative w-[210mm] h-[297mm] bg-[#121212] text-zinc-300 p-[12mm] flex flex-col font-sans overflow-hidden ${className}`}
        style={{ fontFamily: 'Inter, sans-serif', pageBreakAfter: 'always' }}
    >
        <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(ellipse_at_top_left,_var(--tw-gradient-stops))] from-zinc-900/40 via-transparent to-transparent opacity-50 -z-10"></div>
        <div className="absolute bottom-0 right-0 w-full h-full bg-[radial-gradient(ellipse_at_bottom_right,_var(--tw-gradient-stops))] from-teal-950/30 via-transparent to-transparent opacity-70 -z-10"></div>
        <FuturaSmileIcon className="absolute bottom-8 right-8 w-24 h-24 text-white/5 -z-10" />
        {children}
    </div>
);

const PageHeader: React.FC<{ subtitle: string }> = ({ subtitle }) => (
    <header className="w-full flex justify-between items-center pb-3 border-b border-zinc-700/50 flex-shrink-0">
        <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-teal-500 flex items-center justify-center">
                <FuturaSmileIcon className="w-4 h-4 text-white" />
            </div>
            <span className="text-lg font-semibold text-white tracking-wide">FuturaSmile</span>
        </div>
        <span className="text-xs font-medium text-zinc-500 uppercase tracking-widest">{subtitle}</span>
    </header>
);

const PageFooter: React.FC<{ pageNumber: number; totalPages: number; disclaimer?: string }> = ({ pageNumber, totalPages, disclaimer }) => (
    <footer className="w-full mt-auto pt-3 border-t border-zinc-700/50 flex justify-between items-center text-xs text-zinc-500 flex-shrink-0">
        <p>{disclaimer || `FuturaSmile © ${new Date().getFullYear()}. All Rights Reserved.`}</p>
        <p>Page {pageNumber} of {totalPages}</p>
    </footer>
);


// --- REFINED SMILE SCORE ---
const ScoreGauge: React.FC<{ score: number; title: string, color: string }> = ({ score, title, color }) => {
    const radius = 30;
    const circumference = 2 * Math.PI * radius;
    const offset = circumference - (score / 100) * circumference;

    return (
        <div className="p-4 rounded-lg bg-white/5 border border-white/10 w-full flex flex-col items-center text-center">
            <div className="relative w-24 h-24 flex-shrink-0">
                <svg className="w-full h-full" viewBox="0 0 76 76">
                    <circle cx="38" cy="38" r={radius} fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="6" />
                    <circle
                        cx="38" cy="38" r={radius} fill="none" stroke={color} strokeWidth="6"
                        strokeLinecap="round" transform="rotate(-90 38 38)"
                        style={{ strokeDasharray: circumference, strokeDashoffset: offset }}
                    />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-3xl font-bold" style={{ color }}>{score}</span>
                    <span className="text-sm font-medium text-zinc-500 mt-1">/100</span>
                </div>
            </div>
            <p className="font-bold text-base text-white mt-3">{title}</p>
        </div>
    );
};

// --- RADAR CHART COMPONENT ---
const ComparativeRadarChart: React.FC<{ beforeData: any, afterData: any }> = ({ beforeData, afterData }) => {
    // Guard against empty or invalid data
    if (!beforeData?.length || !afterData?.length || beforeData.length < 3) return null;

    const size = 320;
    const center = size / 2;
    const radius = center * 0.7;
    const numAxes = beforeData.length;
    const angleSlice = (Math.PI * 2) / numAxes;
    const levels = 5;

    const gridLevels = Array.from({ length: levels }, (_, i) => {
        const gridRadius = radius * ((i + 1) / levels);
        const points = beforeData.map((_: any, j: number) => {
            const angle = angleSlice * j - Math.PI / 2;
            const x = center + gridRadius * Math.cos(angle);
            const y = center + gridRadius * Math.sin(angle);
            return `${x},${y}`;
        }).join(' ');
        return <polygon key={i} points={points} fill="rgba(255,255,255,0.03)" stroke="rgba(255,255,255,0.1)" strokeWidth="1" />;
    });

    const axesLines = beforeData.map((_: any, i: number) => {
        const angle = angleSlice * i - Math.PI / 2;
        const x = center + radius * Math.cos(angle);
        const y = center + radius * Math.sin(angle);
        return <line key={i} x1={center} y1={center} x2={x} y2={y} stroke="rgba(255,255,255,0.1)" strokeWidth="1" />;
    });

    const axesLabels = beforeData.map((item: any, i: number) => {
        const angle = angleSlice * i - Math.PI / 2;
        const labelRadius = radius * 1.2;
        const x = center + labelRadius * Math.cos(angle);
        const y = center + labelRadius * Math.sin(angle);
        return (
            <text key={i} x={x} y={y} fill="#a1a1aa" fontSize="11" fontWeight="500" textAnchor="middle" dominantBaseline="middle">
                {item.label}
            </text>
        );
    });

    const beforePoints = beforeData.map((item: any, i: number) => {
        const pointRadius = radius * ((item.score ?? 0) / 100);
        const angle = angleSlice * i - Math.PI / 2;
        const x = center + pointRadius * Math.cos(angle);
        const y = center + pointRadius * Math.sin(angle);
        return `${x},${y}`;
    }).join(' ');

    const afterPoints = afterData.map((item: any, i: number) => {
        const pointRadius = radius * ((item.score ?? 0) / 100);
        const angle = angleSlice * i - Math.PI / 2;
        const x = center + pointRadius * Math.cos(angle);
        const y = center + pointRadius * Math.sin(angle);
        return `${x},${y}`;
    }).join(' ');

    return (
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
            <g>
                {gridLevels}
                {axesLines}
                {axesLabels}
                <polygon points={beforePoints} fill="rgba(161, 161, 170, 0.25)" stroke="#a1a1aa" strokeWidth="2" />
                <polygon points={afterPoints} fill="rgba(45, 212, 191, 0.25)" stroke="#2dd4bf" strokeWidth="2" />
            </g>
        </svg>
    );
};

// --- MAIN PDF COMPONENT ---
const CaseReportPDF: React.FC<CaseReportPDFProps> = ({ reportData, originalImageUrl, generatedImageUrl, smileRating, afterSmileRating, onRendered }) => {
    useEffect(() => {
        const timer = setTimeout(() => onRendered(), 200);
        return () => clearTimeout(timer);
    }, [onRendered]);

    if (!reportData || !smileRating || !afterSmileRating) {
        return (
            <Page>
                <p>Loading report data...</p>
            </Page>
        );
    }

    const { reportTitle, introduction, beforeAnalysis, afterVision, transformationAnalysis, treatmentPath, nextSteps } = reportData;
    const scoreBefore = smileRating?.overallSmileScore?.score ?? 0;
    const scoreAfter = afterSmileRating?.overallSmileScore?.score ?? 0;
    const scoreSummary = smileRating?.overallSmileScore?.summary ?? "Analysis complete.";

    const radarChartDataBefore = [
        { label: 'Symmetry & Alignment', score: smileRating?.detailedScores?.symmetryAndAlignment ?? 0 },
        { label: 'Color & Brightness', score: smileRating?.detailedScores?.colorAndBrightness ?? 0 },
        { label: 'Shape & Proportions', score: smileRating?.detailedScores?.shapeAndProportions ?? 0 },
        { label: 'Gum Health & Contour', score: smileRating?.detailedScores?.gumHealthAndContour ?? 0 },
    ];
    const radarChartDataAfter = [
        { label: 'Symmetry & Alignment', score: afterSmileRating?.detailedScores?.symmetryAndAlignment ?? 0 },
        { label: 'Color & Brightness', score: afterSmileRating?.detailedScores?.colorAndBrightness ?? 0 },
        { label: 'Shape & Proportions', score: afterSmileRating?.detailedScores?.shapeAndProportions ?? 0 },
        { label: 'Gum Health & Contour', score: afterSmileRating?.detailedScores?.gumHealthAndContour ?? 0 },
    ];

    const GlassCardWithTag: React.FC<{ title: string; content: string; tag: string }> = ({ title, content, tag }) => {
        const tagColors: { [key: string]: string } = {
            'High Opportunity': 'bg-red-500/20 text-red-400 border-red-500/30',
            'Moderate Opportunity': 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
            'Minor Refinement': 'bg-teal-500/20 text-teal-400 border-teal-500/30',
        };
        const tagClass = tagColors[tag] || 'bg-zinc-500/20 text-zinc-400 border-zinc-500/30';

        return (
            <div className="p-3 bg-white/5 border border-white/10 rounded-lg backdrop-blur-sm relative overflow-hidden">
                <div className="flex justify-between items-start">
                    <p className="font-semibold text-sm text-zinc-100 pr-24">{title}</p>
                    <span className={`absolute top-2.5 right-2.5 text-[10px] font-bold px-2 py-0.5 rounded-full border ${tagClass}`}>
                        {tag}
                    </span>
                </div>
                <p className="text-xs text-zinc-400 mt-1">{content}</p>
            </div>
        );
    };

    const totalPages = 7;

    return (
        <div style={{ background: '#121212', fontFamily: 'Inter, sans-serif' }}>
            {/* Page 1: Cover Page */}
            <Page className="justify-between">
                <div /> {/* Spacer for flexbox */}
                <div className="pl-4">
                    <div className="w-16 h-16 rounded-full bg-gradient-to-br from-teal-500 to-cyan-500 flex items-center justify-center shadow-2xl shadow-teal-500/30 mb-6">
                        <FuturaSmileIcon className="w-8 h-8 text-white" />
                    </div>
                    <h1 className="text-5xl font-extrabold text-white tracking-tight leading-tight max-w-lg">{reportTitle}</h1>
                    <p className="text-lg text-zinc-400 mt-4 max-w-lg">{introduction?.body}</p>
                    <div className="w-24 h-0.5 bg-teal-500 my-8"></div>
                    <div className="text-sm text-zinc-500">
                        <p className="font-semibold text-zinc-400">PERSONALIZED REPORT</p>
                        <p>Generated on: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
                    </div>
                </div>
                <PageFooter pageNumber={1} totalPages={totalPages} disclaimer={nextSteps.disclaimer} />
            </Page>

            {/* Page 2: Before Analysis */}
            <Page>
                <PageHeader subtitle="Current Smile Analysis" />
                <main className="flex-1 flex items-center gap-8 pt-6 min-h-0">
                    <div className="w-[55%] h-full flex flex-col">
                        <h2 className="text-2xl font-bold text-white mb-3">Your Starting Point</h2>
                        <div className="flex-1 rounded-lg overflow-hidden bg-black flex items-center justify-center border border-zinc-800">
                            <img src={originalImageUrl} className="max-w-full max-h-full object-contain" alt="Original Smile" />
                        </div>
                    </div>
                    <div className="w-[45%] h-full flex flex-col justify-center">
                        <p className="text-sm text-zinc-400 mb-4 leading-relaxed">{beforeAnalysis.summary}</p>
                        <ScoreGauge score={scoreBefore} title="Initial Aesthetic Score" color="#facc15" />
                        <div className="mt-6 space-y-3">
                            <h3 className="font-semibold text-zinc-200 mb-2 text-base">Key Diagnostic Areas</h3>
                            {beforeAnalysis.keyAreas.map((item: any, index: number) => (
                                <GlassCardWithTag key={index} title={item.area} content={item.evaluation} tag={item.opportunityLevel} />
                            ))}
                        </div>
                    </div>
                </main>
                <PageFooter pageNumber={2} totalPages={totalPages} />
            </Page>

            {/* Page 3: After Vision */}
            <Page>
                <PageHeader subtitle="The FuturaSmile Vision" />
                <main className="flex-1 flex items-center gap-8 pt-6 min-h-0">
                    <div className="w-[45%] h-full flex flex-col justify-center">
                        <p className="text-sm text-zinc-400 mb-4 leading-relaxed">{afterVision.summary}</p>
                        <ScoreGauge score={scoreAfter} title="Projected Aesthetic Score" color="#2dd4bf" />
                        <div className="space-y-3 mt-6">
                            <h3 className="font-semibold text-zinc-200 mb-2 text-base">Proposed Enhancements</h3>
                            {afterVision.keyChanges.map((item: string, index: number) => (
                                <div key={index} className="flex items-start gap-3 p-3 bg-white/5 rounded-lg border border-white/10">
                                    <CheckCircleIcon className="w-5 h-5 text-teal-400 flex-shrink-0 mt-0.5" />
                                    <p className="text-zinc-300 text-xs">{item}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className="w-[55%] h-full flex flex-col">
                        <h2 className="text-2xl font-bold text-white mb-3">Your Potential, Realized</h2>
                        <div className="flex-1 rounded-lg overflow-hidden bg-black flex items-center justify-center ring-2 ring-teal-500/80 shadow-2xl shadow-teal-500/20">
                            <img src={generatedImageUrl} className="max-w-full max-h-full object-contain" alt="Generated Smile Vision" />
                        </div>
                    </div>
                </main>
                <PageFooter pageNumber={3} totalPages={totalPages} />
            </Page>

            {/* Page 4: Transformation Analysis */}
            <Page>
                <PageHeader subtitle="Transformation Analysis" />
                <main className="flex-1 flex flex-col justify-center items-center pt-6 text-center">
                    <h2 className="text-2xl font-bold text-white">{transformationAnalysis?.headline ?? 'Transformation Analysis'}</h2>
                    <p className="text-sm text-zinc-400 mt-1 max-w-lg mx-auto">{transformationAnalysis?.overallImprovement?.summary ?? ''}</p>
                    <div className="my-6">
                        <ComparativeRadarChart beforeData={radarChartDataBefore} afterData={radarChartDataAfter} />
                    </div>
                    <div className="flex gap-4 items-center justify-center">
                        <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded-full bg-zinc-500 border border-zinc-400"></div>
                            <span className="text-xs text-zinc-400">Initial Smile</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded-full bg-teal-500 border border-teal-400"></div>
                            <span className="text-xs text-teal-400">Projected Smile</span>
                        </div>
                    </div>
                </main>
                <PageFooter pageNumber={4} totalPages={totalPages} />
            </Page>

            {/* Page 5: Comparative Metrics */}
            <Page>
                <PageHeader subtitle="Comparative Metrics" />
                <main className="flex-1 flex flex-col justify-center items-center py-6">
                    <div className="w-full text-center">
                        <h2 className="text-2xl font-bold text-white">Aesthetic Score Improvement</h2>
                        <p className="text-sm text-zinc-400 mt-1 max-w-2xl mx-auto">Here is a detailed comparison of your initial scores versus the projected scores after treatment, highlighting the significant improvements in each key area.</p>
                    </div>
                    <div className="flex w-full max-w-3xl items-center justify-center gap-8 my-8">
                        <ScoreGauge score={scoreBefore} title="Initial Score" color="#a1a1aa" />
                        <div className="text-center">
                            <TransformationArrowIcon className="w-10 h-10 text-teal-400" />
                            <p className="text-2xl font-bold text-teal-400 mt-2">+{transformationAnalysis?.overallImprovement?.scoreChange ?? 0}</p>
                            <p className="text-xs text-zinc-400">Total Points</p>
                        </div>
                        <ScoreGauge score={scoreAfter} title="Projected Score" color="#2dd4bf" />
                    </div>
                    <div className="w-full max-w-3xl space-y-4">
                        {(transformationAnalysis?.comparativeMetrics || []).map((metric: any) => (
                            <div key={metric.area} className="p-4 bg-white/5 border border-white/10 rounded-lg">
                                <p className="font-semibold text-zinc-100">{metric.area}</p>
                                <div className="flex items-center gap-4 mt-2">
                                    <div className="w-full bg-zinc-700/50 rounded-full h-2.5">
                                        <div className="bg-teal-500 h-2.5 rounded-full" style={{ width: `${metric.afterScore}%` }}></div>
                                        <div className="bg-zinc-500 h-2.5 rounded-full -mt-2.5" style={{ width: `${metric.beforeScore}%` }}></div>
                                    </div>
                                    <div className="flex-shrink-0 text-right w-20">
                                        <span className="font-bold text-sm text-zinc-400">{metric.beforeScore}</span>
                                        <span className="text-zinc-500 mx-1">&rarr;</span>
                                        <span className="font-bold text-sm text-teal-400">{metric.afterScore}</span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </main>
                <PageFooter pageNumber={5} totalPages={totalPages} />
            </Page>


            {/* Page 6: Side by Side Visual */}
            <Page>
                <PageHeader subtitle="Visualizing the Transformation" />
                <main className="flex-1 flex flex-col items-center justify-center pt-4 min-h-0">
                    <div className="flex-1 flex items-stretch justify-center w-full gap-5">
                        <div className="w-1/2 flex flex-col items-center">
                            <p className="font-bold text-lg text-zinc-400 mb-2 tracking-widest">BEFORE</p>
                            <div className="flex-1 w-full rounded-lg overflow-hidden border border-zinc-800 bg-black flex items-center justify-center">
                                <img src={originalImageUrl} className="max-w-full max-h-full object-contain" alt="Original Smile" />
                            </div>
                        </div>
                        <div className="flex items-center">
                            <TransformationArrowIcon className="w-10 h-10 text-teal-400" />
                        </div>
                        <div className="w-1/2 flex flex-col items-center">
                            <p className="font-bold text-lg text-teal-400 mb-2 tracking-widest">AFTER</p>
                            <div className="flex-1 w-full rounded-lg overflow-hidden ring-2 ring-teal-500/50 bg-black flex items-center justify-center shadow-lg shadow-teal-500/20">
                                <img src={generatedImageUrl} className="max-w-full max-h-full object-contain" alt="Generated Smile Vision" />
                            </div>
                        </div>
                    </div>
                    <div className="w-full flex-shrink-0 mt-6 text-center">
                        <h2 className="text-2xl font-bold text-white">A Vision of Your Brightest Smile</h2>
                        <p className="text-zinc-400 text-sm mt-1 max-w-xl mx-auto">This side-by-side comparison clearly illustrates the harmonious and aesthetic enhancements proposed in your personalized plan.</p>
                    </div>
                </main>
                <PageFooter pageNumber={6} totalPages={totalPages} />
            </Page>

            {/* Page 7: Treatment Path & Next Steps */}
            <Page>
                <PageHeader subtitle="Your Journey Forward" />
                <main className="flex-1 flex flex-col justify-center items-center gap-8 py-6">
                    <div className="w-full max-w-2xl text-center mb-2">
                        <h2 className="text-3xl font-bold text-white">The Path to Your New Smile</h2>
                        <p className="text-zinc-400 mt-2 text-sm">This report is the first step. Here's a look at the potential journey ahead and how we can achieve these results together.</p>
                    </div>

                    <div className="w-full max-w-3xl p-6 bg-white/5 border border-white/10 rounded-xl">
                        <div className="flex items-start gap-4">
                            <div className="w-12 h-12 bg-teal-900/50 border border-teal-500/30 rounded-lg flex items-center justify-center flex-shrink-0">
                                <PathIcon className="w-7 h-7 text-teal-300" />
                            </div>
                            <div>
                                <h3 className="text-xl font-semibold text-zinc-100">{treatmentPath.headline}</h3>
                                <p className="text-zinc-300 mt-2 leading-relaxed text-sm">{treatmentPath.body}</p>
                            </div>
                        </div>
                    </div>

                    <div className="w-full max-w-3xl p-6 bg-white/5 border border-white/10 rounded-xl">
                        <div className="flex items-start gap-4">
                            <div className="w-12 h-12 bg-teal-900/50 border border-teal-500/30 rounded-lg flex items-center justify-center flex-shrink-0">
                                <NextStepsIcon className="w-7 h-7 text-teal-300" />
                            </div>
                            <div>
                                <h3 className="text-xl font-semibold text-zinc-100">{nextSteps.headline}</h3>
                                <p className="text-zinc-300 mt-2 leading-relaxed text-sm">{nextSteps.body}</p>
                            </div>
                        </div>
                    </div>
                </main>
                <PageFooter pageNumber={7} totalPages={totalPages} />
            </Page>
        </div>
    );
};

export default CaseReportPDF;